import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge, { formatCurrency } from "@/components/StatusBadge";
import { useTableSort } from "@/lib/useTableSort";
import { useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { RotateCcw, Archive } from "lucide-react";

export default function ClosedProjects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/api/projects-closed");
    setItems(data || []);
  };
  useEffect(() => {
    load();
    api.get("/api/users").then((r) => setAllUsers(r.data || [])).catch(() => {});
  }, []);

  const bdMap = useMemo(() => {
    const m = new Map(); allUsers.forEach((u) => m.set(u.id, u)); return m;
  }, [allUsers]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const needle = q.toLowerCase();
    return items.filter((p) =>
      (p.project_name || "").toLowerCase().includes(needle) ||
      (p.project_code || "").toLowerCase().includes(needle) ||
      (p.firm_name_snapshot || "").toLowerCase().includes(needle)
    );
  }, [items, q]);

  const columns = [
    { key: "project_code", label: "Code" },
    { key: "project_name", label: "Project" },
    { key: "firm_name_snapshot", label: "Client" },
    { key: "bd", label: "BD", getValue: (p) => bdMap.get(p.business_development_owner_id)?.name || p.business_development_owner_name_snapshot },
    { key: "value", label: "Value", numeric: true, align: "right", getValue: (p) => p.current_project_value || p.project_amount },
    { key: "received", label: "Received", numeric: true, align: "right", getValue: (p) => p.total_received || 0 },
    { key: "closed_date", label: "Closed On", getValue: (p) => p.updated_at || "" },
  ];
  const { sortedRows, header } = useTableSort(filtered, columns, "closed_date", "desc");

  const reopen = async (p) => {
    const reason = window.prompt(`Reopen "${p.project_name}"? Enter a reason (optional):`);
    if (reason === null) return;
    try {
      await api.post(`/api/projects/${p.id}/reopen`, { reason: reason || null });
      toast.success("Project reopened — moved back to Active");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="closed-projects-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
            <Archive className="w-6 h-6 text-slate-400" /> Closed Projects
          </h1>
          <p className="text-sm text-slate-500 mt-1">Projects that are both <b>Completed</b> and <b>Fully Paid</b>. All invoices, payments and history remain intact.</p>
        </div>
      </div>

      <Card className="border-slate-200 shadow-none p-4 space-y-3">
        <Input placeholder="Search closed projects by name, code or client…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="closed-projects-search" className="max-w-md" />
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="data-table w-full text-sm">
            <thead className="bg-slate-50">{header}</thead>
            <tbody>
              {sortedRows.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`closed-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)}>{p.project_code || "—"}</td>
                  <td className="px-3 py-2 font-medium cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)}>{p.project_name}</td>
                  <td className="px-3 py-2 text-slate-600">{p.firm_name_snapshot}</td>
                  <td className="px-3 py-2 text-slate-600">{bdMap.get(p.business_development_owner_id)?.name || p.business_development_owner_name_snapshot || <span className="text-slate-400 italic">—</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(p.current_project_value || p.project_amount, p.currency)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatCurrency(p.total_received || 0, p.currency)}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {hasRole(user, "admin", "business_development") && (
                      <Button size="sm" variant="outline" onClick={() => reopen(p)} data-testid={`reopen-${p.id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {sortedRows.length === 0 && (
                <tr><td colSpan={8} className="text-center text-slate-500 py-8">No closed projects yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
